import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as ChevronRight } from '../../../assets/images/svg/chevron-right.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import Button from '../../Button/Button';
import ModalBody from '../../Modal/ModalBody';
import SectionLoader from '../../Loader/SectionLoader';
import { getDropoffLocations } from '../../../request/shippingRequest';
import { fetchAccountAddresses } from '../../../store/actions/account/accountActions';
import {
  getCurrentAccountUid,
  selectAccountAddresses,
  selectDefaultMailingAddress,
} from '../../../store/selectors/accountSelectors';

const formatLocationAddress = (loc) => {
  const streetLines = Array.isArray(loc?.StreetLines)
    ? loc.StreetLines.filter(Boolean).join(', ')
    : '';
  return [
    streetLines,
    loc?.City,
    [loc?.StateOrProvinceCode, loc?.PostalCode].filter(Boolean).join(' '),
    loc?.CountryCode,
  ]
    .filter(Boolean)
    .join(', ');
};

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(18)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  padding-top: ${pxToRem(8)};
`;

const Body = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(22)};
  color: ${themeColors.colorTextSecondary};
  margin-top: ${pxToRem(4)};
`;

const ToggleRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  padding: ${pxToRem(16)} 0;
  margin-top: ${pxToRem(20)};
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;

  > svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    color: ${themeColors.colorPrimary};
    transition: transform 0.2s ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '-90deg' : '0deg')});
  }
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(6)};
  margin-top: ${pxToRem(12)};
`;

const ZipLabel = styled.label`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
`;

const SearchRow = styled.div`
  display: flex;
  gap: ${pxToRem(8)};
`;

const ZipInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${pxToRem(10)} ${pxToRem(12)};
  border: none;
  border-radius: ${pxToRem(4)};
  background: #f5f5f5;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextPrimary};

  &:focus {
    outline: 1px solid ${themeColors.colorPrimary};
  }
`;

const SearchButton = styled.button`
  padding: ${pxToRem(10)} ${pxToRem(20)};
  border: 1px solid ${themeColors.colorPrimary};
  border-radius: ${pxToRem(4)};
  background: ${themeColors.colorWhite};
  color: ${themeColors.colorPrimary};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(8)};
  margin-top: ${pxToRem(12)};
  max-height: ${pxToRem(180)};
  overflow-y: auto;
  padding-right: ${pxToRem(4)};
`;

const LocationRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(6)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
`;

const LocationName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
`;

const LocationAddress = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorTextSecondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(40)};
  padding: 0 ${pxToRem(24)};
  text-align: center;

  > svg {
    width: ${pxToRem(36)};
    height: ${pxToRem(36)};
  }
`;

const EmptyText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};
  line-height: 1.5;
`;

const NeedHelpLink = styled.a`
  display: inline-block;
  margin-top: ${pxToRem(4)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const Footer = styled.div`
  margin-top: auto;

  > button {
    width: 100%;
  }
`;

const SellFromHomeShipping = ({ handleNext }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const accountAddresses = useSelector(selectAccountAddresses);
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [locations, setLocations] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (accountUid && !accountAddresses?.length) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, accountAddresses?.length, dispatch]);

  const handleZipChange = (e) => {
    setZipCode(e.target.value.replace(/\D/g, ''));
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const response = await getDropoffLocations({
        countryCode: defaultMailingAddress?.CountryIso3Code,
        postalCode: zipCode,
        addressUid: defaultMailingAddress?.AddressUid,
      });
      setLocations(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      setLocations([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleContinue = () => {
    if (typeof handleNext === 'function') handleNext();
  };

  const supportPhone = t('sellWizard.sfhWizard.phone');

  return (
    <SectionLoader isLoading={isSearching}>
      <ModalBody>
        <Title>{t('sellWizard.sfhWizard.shipping.title')}</Title>
        <Body>{t('sellWizard.sfhWizard.shipping.body')}</Body>

        <ToggleRow
          type="button"
          $expanded={isSearchOpen}
          onClick={() => setIsSearchOpen((open) => !open)}
          data-cy="button-sfh-toggle-fedex-search"
        >
          {t('sellWizard.sfhWizard.shipping.checkNearest')}
          <ChevronRight />
        </ToggleRow>

        {isSearchOpen && (
          <SearchSection>
            <ZipLabel htmlFor="sfh-zip-input">
              {t('sellWizard.sfhWizard.shipping.zipLabel')}
            </ZipLabel>
            <SearchRow>
              <ZipInput
                id="sfh-zip-input"
                inputMode="numeric"
                placeholder={t('sellWizard.sfhWizard.shipping.zipPlaceholder')}
                value={zipCode}
                onChange={handleZipChange}
                data-cy="input-sfh-zip"
              />
              <SearchButton
                type="button"
                onClick={handleSearch}
                data-cy="button-sfh-search"
              >
                {t('sellWizard.sfhWizard.shipping.search')}
              </SearchButton>
            </SearchRow>
          </SearchSection>
        )}

        {isSearchOpen &&
          locations !== null &&
          (locations.length === 0 ? (
            <EmptyState data-cy="text-sfh-no-locations">
              <InfoIcon />
              <EmptyText>
                {t('sellWizard.sfhWizard.shipping.noResultsTitle')}
                <br />
                {t('sellWizard.sfhWizard.shipping.noResultsBody')}
              </EmptyText>
              <NeedHelpLink href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}>
                {t('sellWizard.sfhWizard.needHelp')} {supportPhone}
              </NeedHelpLink>
            </EmptyState>
          ) : (
            <ResultsList data-cy="list-sfh-locations">
              {locations.map((location, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <LocationRow key={`${location.CompanyName}-${idx}`}>
                  <LocationName>{location.CompanyName}</LocationName>
                  <LocationAddress>
                    {formatLocationAddress(location)}
                  </LocationAddress>
                </LocationRow>
              ))}
            </ResultsList>
          ))}

        <Footer>
          <Button
            variant="primary"
            textTransform="none"
            onClick={handleContinue}
            data-cy="button-sfh-continue-review"
          >
            {t('sellWizard.sfhWizard.shipping.continueToReview')}
          </Button>
        </Footer>
      </ModalBody>
    </SectionLoader>
  );
};

SellFromHomeShipping.propTypes = {
  handleNext: PropTypes.func,
};

export default SellFromHomeShipping;
